import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";

import useAnalytics from "@/hooks/use-analytics";
import useAuth from "@/hooks/use-auth";
import type { ExploreSearchParams } from "@/routes/explore/index";

import apiVariables from "../helpers/api-variables";
import { GameStatusFilterEnum, IncludeEnum, SortEnum } from "../types";
import useSelectedSortOption from "./use-selected-sort-option";

export const VALID_FILTERS = [
  "age",
  "coupons",
  "details",
  "difficulty",
  "duration",
  "kind",
  "players",
  "privacy",
  "status",
  "subcategories",
] as const;

export default function useSearchFilters(withTracking = false) {
  const { isUser } = useAuth();
  const [selectedOption] = useSelectedSortOption();
  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { track } = useAnalytics();

  const variables = useMemo(() => {
    const v = apiVariables(search, { isUser, pageSize: 10 });

    if (!isUser) {
      return v;
    }

    v.sortBy = selectedOption;

    if (selectedOption === SortEnum.MY_RATING) {
      v.groupByLocation = false;
    }

    if (search.include) {
      if (search.include === IncludeEnum.NOT_PLAYED) {
        v.excludePlayed = true;
      }
      if (search.include === IncludeEnum.PLAYED) {
        v.escapedOnly = true;
      }
      if (search.include === IncludeEnum.SAVED) {
        v.savedOnly = true;
      }
    }

    return v;
  }, [isUser, search, selectedOption]);

  const eventData = useMemo(() => {
    // biome-ignore lint/suspicious/noExplicitAny: legacy
    const str2array = (str?: string, defaultValue?: any) =>
      typeof str === "string"
        ? str.split(",").map((s) => s.toString().toLowerCase())
        : defaultValue;

    const isScary = search.is_scary?.toLowerCase() ?? "";

    let isScaryValue: boolean | null = null;
    if (isScary === "yes") {
      isScaryValue = true;
    } else if (isScary === "no") {
      isScaryValue = false;
    }

    // biome-ignore lint/suspicious/noExplicitAny: json
    const eventData: Record<string, any> = {
      categories: str2array(search.subcategories),
      difficulty: str2array(search.difficulty),
      filters: [
        search.include === IncludeEnum.SAVED
          ? "wishlist"
          : (search.include || "all").toLowerCase(),
      ],
      include_closed: search.status === GameStatusFilterEnum.PERMANENTLY_CLOSED,
      is_scary: isScaryValue,
      number_of_players: search.players,
      privacy: str2array(search.privacy),
      search_query: search.query,
      sort_order: selectedOption.toLowerCase(),
    };

    if (!eventData.categories && search.categories) {
      eventData.categories = str2array(search.categories);
    }

    return eventData;
  }, [search, selectedOption]);

  const appliedFiltersCount = useMemo(
    () =>
      VALID_FILTERS.reduce(
        (accum, filter) =>
          search[filter as keyof ExploreSearchParams] ? accum + 1 : accum,
        0
      ),
    [search]
  );

  const generateSearchParams = (filters: string[]) => {
    const params: Partial<ExploreSearchParams> = {};
    for (const filter of filters) {
      const value = search[filter as keyof ExploreSearchParams];
      if (value !== undefined) {
        // biome-ignore lint/suspicious/noExplicitAny: dynamic key
        (params as any)[filter] = value;
      }
    }
    return params;
  };

  const clearUrl = (preserveIncludeFilter = false) => {
    const preservedFilters = ["categories", "location", "sortBy"];

    if (preserveIncludeFilter) {
      preservedFilters.push("include");
    }

    return { search: generateSearchParams(preservedFilters), to: pathname };
  };

  const onClear = (preserveIncludeFilter = false) => {
    navigate(clearUrl(preserveIncludeFilter));
  };

  const onSubmit = (form: FormData) => {
    const searchParams = Object.fromEntries(form.entries()) as Record<
      string,
      string
    >;
    navigate({ search: searchParams, to: pathname });
  };

  useEffect(() => {
    if (!withTracking) {
      return;
    }

    track("Games Filtered", eventData);
  }, [withTracking, eventData, track]);

  return {
    appliedFiltersCount,
    clearUrl,
    filterProps: { onClear, onSubmit },
    variables,
  };
}
